/* eslint-disable import/no-cycle */
import { CacheDocumentLocation, OfflineTrackedBooksLoadingState, PersistentStateLoadingState } from '../types';
import { DocumentChunkManifest } from '../types/chunkedDocuments';
import { documentSearchEngine } from './documentSearchEngine';
import { downloadFile as downloadFileImpl } from './file.platform';
import _handleStateUpdateSideEffects from './handleStateUpdateSideEffects';
import { loadImageDataUrls } from './loadImageDataUrls';
import * as models from './models';
import { globalState } from './models';
import _onDatabaseSchemaConflict from './onDatabaseSchemaConflict';
import backgroundPortalGate from './portalGates/toBackground';
import { rxAlert as _rxAlert } from './rxAlert.platform';
import { computeOfflineCacheEnabledByLocation } from './selectors/offlineContent';
import * as otherClientStateUpdaters from './stateUpdaters/clientStateUpdaters/other';
import * as infrastructureRelatedTransientUpdaters from './stateUpdaters/persistentStateUpdaters/infrastructureRelated';
import {
  incrementPersistentStateLoadedDocumentCountByN,
  setAreServerUpdatesBeingAppliedToForeground,
  setAreServerUpdatesBeingFetchedByUser, setChunkedDocDebugInfo,
  setCurrentOfflineTrackedBooksLoadingState,
  setCurrentPersistentStateLoadingState,
  setPersistentStateTotalDocumentsToAddCount,
} from './stateUpdaters/persistentStateUpdaters/infrastructureRelated';
import { setAreAllDatabaseHooksDisabled } from './stateUpdaters/transientStateUpdaters/database';
import * as feedTransientUpdaters from './stateUpdaters/transientStateUpdaters/feed';

export const setProfile: typeof otherClientStateUpdaters.setProfile = (...args) =>
  otherClientStateUpdaters.setProfile(...args);

export const searchInit: typeof documentSearchEngine.init = (...args) =>
  documentSearchEngine.init(...args);
export const searchUpsertDocuments: typeof documentSearchEngine.upsertDocuments = (...args) =>
  documentSearchEngine.upsertDocuments(...args);
export const searchDestroy: typeof documentSearchEngine.destroy = (...args) =>
  documentSearchEngine.destroy(...args);

export { findDocsForParsedDocIds } from './database/getters';

export const onBackgroundStateUpdates: typeof infrastructureRelatedTransientUpdaters.onBackgroundStateUpdates =
  (...args) => infrastructureRelatedTransientUpdaters.onBackgroundStateUpdates(...args);

export const disableAllDatabaseHooks = async () => {
  await setAreAllDatabaseHooksDisabled(true);
};

export const fetchRelatedRSS: typeof feedTransientUpdaters.fetchRelatedRSS = (...args) =>
  feedTransientUpdaters.fetchRelatedRSS(...args);

export const onRSSFeedsLoaded: typeof feedTransientUpdaters.onRSSFeedsLoaded = (...args) =>
  feedTransientUpdaters.onRSSFeedsLoaded(...args);

export const handleStateUpdateSideEffects: typeof _handleStateUpdateSideEffects = (...args) =>
  _handleStateUpdateSideEffects(...args);

export const getCurrentPersistentStateWithDocuments: typeof infrastructureRelatedTransientUpdaters.getCurrentPersistentStateWithDocuments =
  (...args) => infrastructureRelatedTransientUpdaters.getCurrentPersistentStateWithDocuments(...args);
export const runForegroundStateChecksum: typeof infrastructureRelatedTransientUpdaters.runForegroundStateChecksum =
  (...args) => infrastructureRelatedTransientUpdaters.runForegroundStateChecksum(...args);

export const updatePersistentStateLoadingState = async (loadingState: PersistentStateLoadingState) => {
  setCurrentPersistentStateLoadingState(loadingState);
};
export const updateChunkedDocDebugInfo = async (allDocIds: string[], currentDocIdsLoading: string[], currentChunkIdsLoading: string[]) => {
  return setChunkedDocDebugInfo(allDocIds, currentDocIdsLoading, currentChunkIdsLoading);
};
export const updateCurrentOfflineTrackedBooksLoadingState = async (loadingState: OfflineTrackedBooksLoadingState) => {
  setCurrentOfflineTrackedBooksLoadingState(loadingState);
};
export const updatePersistentStateTotalNumberOfDocumentsToLoad = async (documentsCount: string) => {
  setPersistentStateTotalDocumentsToAddCount(documentsCount);
};

export const updatePersistentStateLoadedDocumentCountByN = async (n: number) => {
  incrementPersistentStateLoadedDocumentCountByN(n);
};

export const setAreServerChangesBeingAppliedToForeground = async (changesAreBeingUpdated: boolean) => {
  setAreServerUpdatesBeingAppliedToForeground(changesAreBeingUpdated);
};

export const onConsumeServerToForegroundUpdatesFinished = async () => {
  setAreServerUpdatesBeingFetchedByUser(false);
};

export const rxAlert: typeof _rxAlert = _rxAlert;

export const onDatabaseSchemaConflict: typeof _onDatabaseSchemaConflict = _onDatabaseSchemaConflict;

export const isStaffProfile: typeof models.isStaffProfile = (...args) => models.isStaffProfile(...args);

export function getProfileId(): number | undefined {
  return globalState.getState().client.profile?.profile_id;
}

export const downloadFile = downloadFileImpl;

export function loadChunksFromSpineAndManifest(sortedSpineIds: string[], manifest: DocumentChunkManifest | undefined, docId: string) {
  return backgroundPortalGate.loadChunksFromSpineAndManifest(sortedSpineIds, manifest, docId);
}

export function loadImagesAsDataUrlsFromManifest(
  manifest: DocumentChunkManifest,
  absoluteFilepaths: string[],
  docId: string,
) {
  return loadImageDataUrls(manifest, absoluteFilepaths, docId);
}

export async function getOfflineCacheEnabledByLocation() {
  const fromState = globalState.getState()?.client?.offlineCacheEnabledByLocation;
  return computeOfflineCacheEnabledByLocation(fromState);
}

export async function getExperimentalOfflineLoadingEnabled(): Promise<boolean> {
  return globalState.getState()?.persistent?.settings?.experimentalOfflineLoadingEnabled ?? false;
}

export async function setCurrentDownloadingLocation(location: CacheDocumentLocation | null) {
  await models.updatePropertyInState('currentDownloadingLocation', location, {
    eventName: 'setCurrentDownloadingLocation',
    userInteraction: null,
  });
}

export function getChunkManifestForDocument(docId: string): DocumentChunkManifest | undefined {
  return globalState.getState().transientDocumentsData?.[docId]?.manifest;
}
